import axios from 'axios';
import { supabase, getAccessToken } from './supabaseClient';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Request interceptor — injects the Supabase access token.
 *
 * Priority:
 *  1. Shared module-level token (set by AuthContext on every state change)
 *  2. supabase.auth.getSession() as fallback
 */
api.interceptors.request.use(async (config) => {
  let token = getAccessToken();

  if (!token) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      token = session?.access_token ?? null;
    } catch (_) {}
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn('[API] No token for', config.url);
  }
  return config;
});

// Retry once on 401 after refreshing the session
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config;
    if (error.response?.status === 401 && !orig._retried) {
      orig._retried = true;
      try {
        const { data: { session } } = await supabase.auth.refreshSession();
        if (session?.access_token) {
          orig.headers.Authorization = `Bearer ${session.access_token}`;
          return api.request(orig);
        }
      } catch (_) {}
    }
    return Promise.reject(error);
  }
);

export const fetchInvoices = (status) =>
  api.get('/api/invoices', { params: status ? { status } : {} });
export const updateInvoice = (id, data) =>
  api.patch(`/api/invoices/${id}`, data);
export const rejectInvoice = (id) =>
  api.post(`/api/invoices/${id}/reject`);
export const batchReject = (ids) =>
  api.post('/api/invoices/batch-reject', { ids });
export const approveInvoice = (id) =>
  api.post(`/api/invoices/${id}/approve`);
export const batchApprove = (ids) =>
  api.post('/api/invoices/batch-approve', { ids });
export const mergeInvoices = (ids, clientEmail) =>
  api.post('/api/invoices/merge', { ids, client_email: clientEmail });
export const processClickupTask = (taskId) =>
  api.post('/api/process/clickup-task', { task_id: taskId });
export const applyDiscount = (id, type, value, reason) =>
  api.post(`/api/invoices/${id}/discount`, { type, value, reason });
export const getWebhookInfo = () =>
  api.get('/api/webhook-info');
export const getBillingRate = () =>
  api.get('/api/billing-rate');
export const fetchHistory = (status) =>
  api.get('/api/invoices/history', { params: status && status !== 'all' ? { status } : {} });
export const restoreInvoice = (id) =>
  api.post(`/api/invoices/${id}/restore`);

export default api;
